import { React, useState, useRef, useEffect } from 'react';
import { Minus, Plus } from "lucide-react";
import { useOutletContext } from 'react-router';

const TERMINAL_PROMPT = "root@127.0.0.1 ~ %";
const TERMINAL_COMMANDS = {
  tldr: "I'm rolam, a designer-engineer. I build interfaces that enable users to get things done without friction.",
  contact: "email | hi@rolam.co\nLinkedIn | /in/rolamworks",
  whoami: "i don't know...",
  pwd: "/rolam/design/projects",
  help: "tldr\ncontact\nfact\nwhoami\npwd\nclear\nhelp",
  secret: "01011001 01101111 01110101 00100000 01110111 01101001 01101100 01101100 00100000 01101000 01100001 01110110 01100101 00100000 01110100 01101111 00100000 01110100 01110010 01111001 00100000 01101000 01100001 01110010 01100100 01100101 01110010 00100000 01110100 01101000 01100001 01101110 00100000 01110100 01101000 01100001 01110100",
};
const DESIGN_FACTS = [
  "Did you know that users form an initial impression of an interface in a fraction of a second?",
  "Did you know that consistent spacing can make an interface feel easier to understand?",
  "Did you know that clear labels usually outperform clever icons?",
];

const evaluateBasicMath = (command) => {
  const match = command.match(/^(-?\d+(?:\.\d+)?)\s*([+\-*/])\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;

  const left = Number(match[1]);
  const right = Number(match[3]);
  const operations = {
    "+": () => left + right,
    "-": () => left - right,
    "*": () => left * right,
    "/": () => right === 0 ? "cannot divide by zero" : left / right,
  };

  return String(operations[match[2]]());
};

const getTerminalResponse = (command) => {
  const normalizedCommand = command.trim().toLowerCase();
  let response = TERMINAL_COMMANDS[normalizedCommand];

  if (normalizedCommand === "fact") {
    response = DESIGN_FACTS[Math.floor(Math.random() * DESIGN_FACTS.length)];
  }

  response ??= evaluateBasicMath(normalizedCommand);
  response ??= `command not found: ${normalizedCommand.split(/\s+/)[0]}\nto see a list of commands, run 'help'`;

  return `${TERMINAL_PROMPT} ${command}\n${response}`;
};

const shouldAnimateReadme = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 769px)").matches &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const ReadmeContent = ({ content }) => {
  const animate = useRef(shouldAnimateReadme()).current;
  const [visibleCharacters, setVisibleCharacters] = useState(() =>
    animate ? 0 : content.length
  );

  useEffect(() => {
    if (!animate) return;

    let animationFrame;
    const startedAt = performance.now();

    const revealText = (now) => {
      const nextCharacter = Math.min(
        content.length,
        Math.floor(now - startedAt)
      );
      setVisibleCharacters(nextCharacter);

      if (nextCharacter < content.length) {
        animationFrame = requestAnimationFrame(revealText);
      }
    };

    animationFrame = requestAnimationFrame(revealText);
    return () => cancelAnimationFrame(animationFrame);
  }, [animate, content]);

  return (
    <p
      className={`readme-content ${animate ? "typing" : ""}`}
      aria-label={content}
    >
      {content.slice(0, visibleCharacters)}
    </p>
  );
};

const RightPanel = ({
  readme,
  title,
}) => {
  const {
    panelRightOpen,
    terminalOpen,
    handleMinimizeRight,
    handleMinimizeTerminal,
    handleMaximizeRight,
    handleMaximizeTerminal,
  } = useOutletContext();

  const [terminalFocus, setTerminalFocus] = useState(true);
  const [terminalResponses, setTerminalResponses] = useState([]);

  const promptRef = useRef(null);
  const terminalRef = useRef(null);

  const handleTerminlPromptSubmit = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const command = promptRef.current.innerText.trim();

      if (command.toLowerCase() === 'clear') {
        promptRef.current.innerText = "";
        setTerminalResponses([]);
      } else if (command) {
        setTerminalResponses((responses) => [
          ...responses,
          getTerminalResponse(command),
        ]);
        promptRef.current.innerText = "";
      }
    } else if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      e.preventDefault();
    }
  }

  useEffect(() => {
    terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [terminalResponses]);

  useEffect(() => {
    const matrix = [
      "Wake up, user...",
      "You think these are pixels you're looking at now?",
      "Follow the white cursor.",
      "There is no button",
      "The interface is everywhere. It is all around us.",
      "Accessibility is bliss.",
      "I know design!",
      "Remember... all I'm offering is good design. Nothing more.",
      "Welcome to the desert of the screens."
    ];

    const handleMatrixMessage = (visitCount) => {
      setTerminalResponses((responses) => [
        ...responses,
        `${TERMINAL_PROMPT}\n${matrix[visitCount]}`,
      ]);
    }

    let visitCount = parseInt(localStorage.getItem("VISITS") || "0", 10);

    if (visitCount <= 8) {
      setTimeout(() => {
        handleMatrixMessage(visitCount);
        visitCount++;
        localStorage.setItem("VISITS", visitCount);
      }, 60000)
    }

    // eslint-disable-next-line
  }, []);

  return (
    <div className={`panel right ${panelRightOpen ? "open" : "collapsed"} ${terminalOpen ? "terminal-open" : ""}`}>
      <div className="subpanel readme-panel">
        <div className="subpanel-top">
          <label>Readme</label>
          <button className="min-max-button" onClick={() => panelRightOpen ? handleMinimizeRight() : handleMaximizeRight()}>
            {panelRightOpen ? <Minus size={"1rem"} /> : <Plus size={"1rem"} />}
          </button>
        </div>
        <div className="readme">
          <ReadmeContent content={readme} key={readme} />
        </div>
      </div>
      <div className={`subpanel terminal ${terminalOpen ? "open" : "collapsed"}`} onClick={() => promptRef.current.focus()}>
        <div className="subpanel-top">
          <label>Terminal</label>
          <button className="min-max-button" onClick={() => terminalOpen ? handleMinimizeTerminal() : handleMaximizeTerminal()}>
            {terminalOpen ? <Minus size={"1rem"} /> : <Plus size={"1rem"} />}
          </button>
        </div>
        <div className="terminal-content" ref={terminalRef}>
          {terminalResponses.map((response, index) => {
            return (
              <div key={index} className="terminal-response" value={response} >{response}</div>
            )
          })}
          <span>root@127.0.0.1 ~ %</span>
          <span
            ref={promptRef}
            onFocus={() => setTerminalFocus(true)}
            onBlur={() => setTerminalFocus(false)}
            onKeyDown={(e) => handleTerminlPromptSubmit(e)}
            className={terminalFocus ? "terminal-prompt active" : "terminal-prompt"}
            contentEditable
          ></span>
        </div>
      </div>
    </div>
  );
}

export default RightPanel;